// Copyright (c) Docugami, Inc. All rights reserved.

import { DgButton } from "components/common/DgCommonStyledComponents";
import { dgFontFamily, UserInfo } from "components/common/Fonts";
import { DocumentPreviewPageRenderer } from "components/file_management/presentation/DocumentPreviewRenderer";
import { homeGray } from "components/shell/presentation/AppStyles";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { NamedResource } from "networking/WorkPathApi";
import { useEffect } from "react";
import { ensureDocSetDocuments } from "store/clusters/actions";
import { downloadAssistedAuthoringDocument } from "store/filemanager/actions";
import styled from "styled-components";
import { WorkPathCanvasProps } from "work_path/WorkPathCanvas";
import { ChooseDocumentStepHeader } from "./WorkPathHeaders";
import { WorkPathStepCanvas } from "./WorkPathStepCanvas";
import { StepCanvas } from "./WorkPathStyles";

const InfoText = styled(UserInfo)`
    color: ${homeGray};
    font-weight: 400;
    font-size: 26px;
    line-height: 35px;
`;

const GridContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 20px;
    height: 100%;
    min-height: 0;
    
    & > * {
        flex: 1;
    }
`;

const ContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 30px;
    padding: 40px;
    box-sizing: border-box;
    align-items: center;
    justify-content: center;
    text-align: center;
    overflow: auto;
`;

const FileName = styled(InfoText)`
    font-weight: 700;
    font-size: 32px;
    line-height: 38px;
`;

const ActionButton = styled(DgButton)`
    display: flex;
    width: fit-content;
`;

const StepContainer = styled(StepCanvas)`
    flex-direction: column;
    gap: 15px;
    height: 100%;
    min-height: 0;
`

const TitleText = styled.span`
    font-family: ${dgFontFamily};
    font-size: 28px;
    font-weight: 400;
    line-height: 34px;
    width: 100%;
    padding-bottom: 10px;
    border-bottom: 1px solid #D0CDCD;
`;

const HelpText = styled(InfoText)`
    text-align: left;
    font-size: 16px;
`;

export const AssistedAuthoringCanvas = (props: WorkPathCanvasProps): JSX.Element => {
    const fileName = `${props.workPath.name}.docx`;
    const documentId = props.workPath.resources[NamedResource.DocumentId];
    const document = useAppSelector(state => state.documents.indexed[documentId]?.clusterDocument);
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!document && !!props.workPath.clusterId) {
            dispatch(ensureDocSetDocuments(props.workPath.clusterId))
        }
    }, [dispatch, props.workPath.clusterId, document]);

    async function downloadFile() {
        if (!document?.folderId) {
            return;
        }

        await downloadAssistedAuthoringDocument(document.folderId, fileName);
    }

    return (
        <WorkPathStepCanvas>
            <ChooseDocumentStepHeader workPath={props.workPath} />
            <StepContainer>
                <TitleText>STARTER DOCUMENT</TitleText>
                <GridContainer>
                    <ContentContainer>
                        <InfoText>The document you are using for this project is:</InfoText>
                        <FileName>{document?.filename}</FileName>
                        <InfoText>Download your document:</InfoText>
                        <ActionButton disabled={!document} onClick={downloadFile}>DOWNLOAD</ActionButton>
                        <DownloadAddInHelpText />
                    </ContentContainer>
                    <DocumentPreviewPageRenderer clusterId={props.workPath.clusterId} documentId={documentId} />
                </GridContainer>
            </StepContainer>
        </WorkPathStepCanvas>
    );
}

const DownloadAddInHelpText = (): JSX.Element => {
    return <HelpText>If you have never used <b>Docugami for Word</b> before, download the add-in <a target='_blank' rel='noopener noreferrer' href='https://appsource.microsoft.com/en-us/product/office/WA200002230?corrid=4c1210d9-1826-5916-5123-2805d6922ca9&src=office&exp=kyyw'>here</a> to get started.</HelpText>
}